import lodash from 'lodash';
import { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';

import RemoveIconAlt from '../../svgs/RemoveIconAlt';
import SearchIcon from '../../svgs/SearchIcon';
import AutoResizeableMultilineInput from '../common/AutoResizeableMultilineInput';

import type { FC, FocusEvent, FormEvent } from 'react';

type Props = {
  placeholder?: string;
  iconRight?: boolean;
  handleAction: (textValue: string) => void;
  hideClearAll?: boolean;
  initialValue?: string;
  radius?: string;
  expandHeight?: boolean;
  hideIcons?: boolean;
  width?: string;
  autoSubmitDelay?: number;
};
const SearchInput: FC<Props> = props => {
  const {
    hideClearAll = false,
    initialValue = '',
    radius = '100px',
    expandHeight = false,
    width,
    autoSubmitDelay,
  } = props;
  const [textValue, setTextValue] = useState<string>(initialValue);
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const [inputHeight, setInputHeight] = useState<number>(40);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    props.handleAction(textValue);
  };

  const debouncedSubmit = useCallback(
    lodash.debounce(
      (textValue: string) => props.handleAction(textValue),
      autoSubmitDelay,
    ),
    [props.handleAction],
  );

  const onTextChange = (textValue: string) => {
    setTextValue(textValue);
    if (autoSubmitDelay || textValue === '') {
      debouncedSubmit(textValue);
    }
  };

  const handleAdjustHeight = (textarea: HTMLTextAreaElement) => {
    if (textarea.scrollHeight < 30) return;
    setInputHeight(textarea.scrollHeight);
    textarea.style.height = textarea.scrollHeight + 'px';
  };

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const handleAdjustInputHeight = (e: FormEvent<HTMLTextAreaElement>) => {
    const textarea = e.target as HTMLTextAreaElement;
    handleAdjustHeight(textarea);
  };

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const handleAdjustHeightOnFocus = (
    e: FocusEvent<HTMLTextAreaElement, Element>,
  ) => {
    const textarea = e.target as HTMLTextAreaElement;
    handleAdjustHeight(textarea);
  };

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const handleAdjustHeightOnBlur = (
    e: FocusEvent<HTMLTextAreaElement, Element>,
  ) => {
    const textarea = e.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    let newHeight = 35;
    setInputHeight(newHeight);
    textarea.style.height = newHeight + 'px';
  };

  return (
    <Form
      iconRight={props.iconRight}
      radius={radius}
      expandHeight={expandHeight}
      onSubmit={handleSubmit}
      width={width}
    >
      {!props.hideIcons && (
        <Search>
          <div className="search">
            <SearchIcon width="14" height="14" fillColor="var(--text-muted)" />
          </div>
        </Search>
      )}
      {expandHeight ? (
        <AutoResizeableMultilineInput
          hasOutline={false}
          hasBorder={false}
          borderOnFocus={false}
          placeholder={props.placeholder || 'Describe your voice'}
          width="100%"
          customHeight="20px"
          getValue={(value: string) => {
            onTextChange(value);
            if (value === '') {
              props.handleAction(value);
            }
          }}
          handleOnEnter={() => props.handleAction(textValue)}
        />
      ) : (
        <Input
          type="text"
          value={textValue}
          onChange={e => {
            e.stopPropagation();
            onTextChange(e.target.value);
            if (e.target.value === '') {
              props.handleAction(e.target.value);
            }
          }}
          placeholder={props.placeholder || 'Search'}
        />
      )}
      {/* Trails the input now that the search icon leads. Still gated on
          `iconRight` so exactly the same callers show a clear button as before —
          only its position changed. */}
      {!props.hideIcons &&
      props.iconRight &&
      textValue?.length &&
      !hideClearAll ? (
        <ClearAll onClick={() => onTextChange('')}>
          <RemoveIconAlt />
        </ClearAll>
      ) : null}
    </Form>
  );
};

export default SearchInput;

const Form = styled.form<{
  iconRight?: boolean;
  radius: string;
  expandHeight?: boolean;
  width?: string | undefined;
}>`
  display: flex;
  /* Matches the homepage search field (Home/RecentContentFilters), the reference
     implementation of the documented field pattern. Was height: fit-content,
     which left the rail's search noticeably taller than it. The expandHeight
     variant opts back out for the multi-line callers. */
  height: 28px;
  box-sizing: border-box;
  padding: 0 12px;
  align-items: center;
  gap: 8px;
  border-radius: ${props => props.radius};
  border: 1px solid var(--input-border, var(--border));
  background-color: var(--input-background, transparent);
  color: var(--text-secondary);
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: var(--text-muted);
  }

  /* Neutral gray, never gold: this was border-color: var(--accent), which the
     field pattern rules out ("focus is neutral gray, never green or gold") and
     which also collided with gold's meaning elsewhere in the rail — selection.
     The 1px ring was missing too. */
  &:focus-within {
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  ${props =>
    props.width &&
    css`
      width: ${props.width};
    `}

  /* The search icon always leads, the near-universal convention for a search
     field. The iconRight prop used to flip this row, but it also gates whether
     the clear button renders, so it is left in place doing only that. */
  ${props =>
    props.expandHeight &&
    css`
      height: auto;
      padding: 8px 12px;
    `}
`;

/** Clear button, trailing the input. Was nested inside `Search` beside the icon. */
const ClearAll = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity var(--transition-fast) ease;

  &:hover {
    opacity: 1;
  }
`;

const Search = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  .clear-all {
    display: flex;
    align-items: center;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 150ms ease;
    &:hover {
      opacity: 1;
    }
  }
  .search {
    display: flex;
    align-items: center;
  }
`;

const Input = styled.input`
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  border: none;
  outline: none;
  background: none;
  flex: 1;
  min-width: 0;
  height: 36px;

  &::placeholder {
    color: var(--text-muted);
  }
`;
